/**
 * Sidebar for the unified Tests page — search + filters + group-by
 * + grouped test list.
 *
 * Replaces the left panels of both old pages (`TestPlan.tsx` had a
 * flat case list with filters; `TestSuites.tsx` had a suite list
 * with a virtual "All Test Scripts" entry). The unified shape: one
 * scrollable list of every test, grouped by the dimension the
 * operator picks, with suite-headers themselves selectable to swap
 * the right pane into suite-detail mode.
 *
 * **State surfaces:**
 *   - Selection + group-by + sort live in the URL (the
 *     `useTestsSelection()` hook owns those).
 *   - Filter dropdowns (status / intent / health / type) +
 *     selected tags live in localStorage via `usePersistedState`
 *     — muscle memory across reloads, but no URL clutter.
 *   - Search text lives in the URL via `useQueryParamState` (#1217)
 *     so a navigate-away-and-return restores the operator's active
 *     query. The hook strips the param on empty so a cleared search
 *     leaves no `?q=` residue in the URL.
 *
 * **Group-by behavior:**
 *   - `suite` (default): one collapsible section per suite, an
 *     "(no suite)" bucket for orphans. Suite headers are themselves
 *     buttons that select the suite (right pane → SuiteDetail).
 *     Tests inside select the test (right pane → TestDetail).
 *   - `none`: a flat sorted list, no headers.
 *   - `status` / `intent`: collapsible sections by that field.
 *     Section headers are display-only (clicking expands/collapses;
 *     no selection-side effect).
 *
 * **Health icon** comes from `healthIcon()` in
 * `components/TestCaseListItem.tsx` — same logic the other test
 * pages already use.
 */
import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, X } from 'lucide-react'
import { Input, KebabMenu, Select } from '@/components/ui'
import { SearchInput } from '@/components/SearchInput'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useQueryParamState } from '@/hooks/useQueryParamState'
import { healthIcon } from '@/components/TestCaseListItem'
import { formatDateTime } from '@/lib/utils'
import { selectedSidebarClasses } from '@/lib/styles'
import type { PlanTestCase, TestCase, TestSuite } from '@/types'
import type { GroupBy, Selection, SortBy } from '../types'

const PRIORITY_COLORS: Record<string, string> = {
  P0: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  P1: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  P2: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  P3: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
}

const PRIORITY_ORDER: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 }
const STATUS_ORDER: Record<string, number> = { scripted: 0, planned: 1, blocked: 2 }

const NO_SUITE_KEY = '__no_suite__'

export interface TestsSidebarProps {
  cases: PlanTestCase[]
  suites: TestSuite[]
  scriptByCaseId: Record<string, TestCase>
  selection: Selection
  groupBy: GroupBy
  sortBy: SortBy
  onSelect: (sel: Selection) => void
  onGroupByChange: (next: GroupBy) => void
  onSortChange: (next: SortBy) => void
  /** Open the suite with its name ready to edit (#2194). Renaming already
   * lives in the suite's own pane; this opens it there rather than adding a
   * second way to do the same thing. */
  onRenameSuite: (suiteId: string) => void
  /** Ask to delete the suite. The page owns the confirmation, because it
   * also owns clearing the selection afterwards. */
  onDeleteSuite: (suite: TestSuite) => void
  /** Narrow the list to these tests: the ones that count toward no
   * active suggestion, set by "Show them" on the Proposed tab. Applied on
   * top of every other filter and shown as a removable chip. `null` or
   * absent means no narrowing. */
  onlyIds?: ReadonlySet<string> | null
  /** Remove the `onlyIds` narrowing. */
  onClearOnlyIds?: () => void
}

export function TestsSidebar({
  cases,
  suites,
  scriptByCaseId,
  selection,
  groupBy,
  sortBy,
  onSelect,
  onGroupByChange,
  onSortChange,
  onRenameSuite,
  onDeleteSuite,
  onlyIds,
  onClearOnlyIds,
}: TestsSidebarProps) {
  // Search lives in the URL so navigate-away-and-return restores the
  // operator's active query (#1217). `useQueryParamState` writes via
  // `history.replaceState` so per-keystroke updates don't pollute
  // browser history, and strips `?q=` on empty so a cleared search
  // leaves no residue.
  const [searchText, setSearchText] = useQueryParamState('q', '')

  // Filters: localStorage-backed (muscle memory across reloads).
  // Keys prefixed `tests-` so they don't collide with TestPlan's
  // `testplan-` keys during the transition period.
  const [filterStatus, setFilterStatus] = usePersistedState('tests-filter-status', 'all')
  const [filterIntent, setFilterIntent] = usePersistedState('tests-filter-intent', 'all')
  const [filterHealth, setFilterHealth] = usePersistedState<'all' | 'passing' | 'failing' | 'unverified'>('tests-filter-health', 'all')
  const [filterType, setFilterType] = usePersistedState<'all' | 'test' | 'fixture'>('tests-filter-type', 'all')
  const [filterTags, setFilterTags] = usePersistedState<string[]>('tests-filter-tags', [])

  // Collapsed group keys. Default: all expanded. Stored locally
  // (transient) so collapse-state doesn't leak across browser
  // sessions — operators expect a fresh view on revisit.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

  function toggleGroup(key: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Reverse-index: every suite a case belongs to. The sidebar item
  // for a test doesn't currently surface its full suite list (the
  // detail pane does), but the suite header counts and the "no
  // suite" bucket both need this.
  const suitesByCaseId = useMemo(() => {
    const map = new Map<string, TestSuite[]>()
    for (const s of suites) {
      for (const cid of s.testCaseIds) {
        const list = map.get(cid) ?? []
        list.push(s)
        map.set(cid, list)
      }
    }
    return map
  }, [suites])

  const uniqueIntents = useMemo(() => {
    const set = new Set<string>()
    for (const c of cases) if (c.intent) set.add(c.intent)
    return Array.from(set).sort()
  }, [cases])

  const uniqueTags = useMemo(() => {
    const set = new Set<string>()
    for (const c of cases) for (const tag of c.categories ?? []) set.add(tag)
    return Array.from(set).sort()
  }, [cases])

  // Filtered + sorted flat list. Group-by is applied on top.
  const filtered = useMemo(() => {
    let out = cases
    if (onlyIds) out = out.filter((c) => onlyIds.has(c.id))
    if (searchText) {
      const q = searchText.toLowerCase()
      out = out.filter((c) => c.name.toLowerCase().includes(q))
    }
    if (filterStatus !== 'all') out = out.filter((c) => c.status === filterStatus)
    if (filterIntent !== 'all') out = out.filter((c) => c.intent === filterIntent)
    if (filterType !== 'all') out = out.filter((c) => (c.type ?? 'test') === filterType)
    if (filterHealth !== 'all') {
      out = out.filter((c) => {
        const merged = scriptByCaseId[c.id]
        const p = merged?.lastPassedAt ? new Date(merged.lastPassedAt as unknown as string).getTime() : 0
        const f = merged?.lastFailedAt ? new Date(merged.lastFailedAt as unknown as string).getTime() : 0
        if (filterHealth === 'unverified') return !p && !f
        if (filterHealth === 'failing') return f > 0 && f >= p
        if (filterHealth === 'passing') return p > 0 && p > f
        return true
      })
    }
    if (filterTags.length > 0) {
      out = out.filter((c) => (c.categories ?? []).some((tag) => filterTags.includes(tag)))
    }
    out = [...out].sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          return (PRIORITY_ORDER[a.priority] ?? 4) - (PRIORITY_ORDER[b.priority] ?? 4)
        case 'status':
          return (STATUS_ORDER[a.status] ?? 4) - (STATUS_ORDER[b.status] ?? 4)
        case 'newest':
          return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
        case 'name':
        default:
          return a.name.localeCompare(b.name)
      }
    })
    return out
  }, [cases, onlyIds, scriptByCaseId, searchText, filterStatus, filterIntent, filterType, filterHealth, filterTags, sortBy])

  // Group the filtered list. Each group: { key, label, cases[],
  // headerSelectable, suite? } — the suite reference is only on
  // suite-grouped headers so the header onClick can select the
  // suite (right pane → SuiteDetail). Other groupings have
  // display-only headers.
  interface Group {
    key: string
    label: string
    count: number
    cases: PlanTestCase[]
    suite?: TestSuite
  }
  const groups = useMemo<Group[]>(() => {
    if (groupBy === 'none') {
      return [{ key: '__all__', label: 'All tests', count: filtered.length, cases: filtered }]
    }
    if (groupBy === 'status' || groupBy === 'intent') {
      const map = new Map<string, PlanTestCase[]>()
      for (const c of filtered) {
        const key = (groupBy === 'status' ? c.status : c.intent) ?? '(none)'
        const list = map.get(key) ?? []
        list.push(c)
        map.set(key, list)
      }
      return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, cs]) => ({ key, label: key, count: cs.length, cases: cs }))
    }
    // groupBy === 'suite': order suites first (by creation), then
    // an "(no suite)" bucket for orphans. Cases can belong to
    // multiple suites — they show under each.
    const result: Group[] = []
    for (const s of suites) {
      const memberCases = filtered.filter((c) => s.testCaseIds.includes(c.id))
      // Show empty suites in the unfiltered view (so operators see
      // "Smoke (0)" and can add to it). Hide empty suites when the
      // user is filtering — search shouldn't surface sections with
      // no hits. The filter happens at the .filter() below.
      result.push({ key: `suite-${s.id}`, label: s.name, count: memberCases.length, cases: memberCases, suite: s })
    }
    const orphans = filtered.filter((c) => !suitesByCaseId.has(c.id))
    if (orphans.length > 0) {
      result.push({ key: NO_SUITE_KEY, label: '(no suite)', count: orphans.length, cases: orphans })
    }
    return result.filter((g) => g.count > 0 || (g.suite && !searchText && !onlyIds))
  }, [groupBy, filtered, suites, suitesByCaseId, scriptByCaseId, searchText, onlyIds])

  return (
    <div className="h-full flex flex-col bg-card">
      {/* Search + group-by + sort row */}
      <div className="p-2 border-b space-y-2">
        <SearchInput value={searchText} onChange={setSearchText} placeholder="Search tests..." />
        {onlyIds && (
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/35 bg-primary/10 px-2.5 py-0.5 text-[11.5px] font-medium text-primary">
              Not counted toward coverage · {onlyIds.size}
              {onClearOnlyIds && (
                <button
                  type="button"
                  onClick={onClearOnlyIds}
                  aria-label="Clear this filter"
                  className="opacity-70 hover:opacity-100"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          </div>
        )}
        <div className="flex gap-1.5">
          <Select
            options={[
              { value: 'suite', label: 'Group: Suite' },
              { value: 'none', label: 'Group: None' },
              { value: 'status', label: 'Group: Status' },
              { value: 'intent', label: 'Group: Intent' },
            ]}
            value={groupBy}
            onChange={(e) => onGroupByChange(e.target.value as GroupBy)}
            className="flex-1 text-xs h-7"
          />
          <Select
            options={[
              { value: 'priority', label: 'Sort: Priority' },
              { value: 'status', label: 'Sort: Status' },
              { value: 'name', label: 'Sort: Name' },
              { value: 'newest', label: 'Sort: Newest' },
            ]}
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value as SortBy)}
            className="flex-1 text-xs h-7"
          />
        </div>
        {/*
          Four filters in a two-wide grid, not one flex row (#2086). Four
          across left each control about 19px of text room in the resizable
          sidebar, so every label clipped to a fragment ("All St", "All Inte",
          "All He", "All T"). Two per row — the same shape as the group-by/sort
          row above — keeps the full labels readable all the way down to the
          panel's 260px minimum width.
        */}
        <div className="grid grid-cols-2 gap-1.5">
          <Select
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'planned', label: 'Planned' },
              { value: 'scripted', label: 'Scripted' },
              { value: 'blocked', label: 'Blocked' },
            ]}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-xs h-7"
          />
          <Select
            options={[
              { value: 'all', label: 'All Intents' },
              ...uniqueIntents.map((i) => ({ value: i, label: i })),
            ]}
            value={filterIntent}
            onChange={(e) => setFilterIntent(e.target.value)}
            className="text-xs h-7"
          />
          <Select
            options={[
              { value: 'all', label: 'All Health' },
              { value: 'passing', label: 'Passing' },
              { value: 'failing', label: 'Failing' },
              { value: 'unverified', label: 'Unverified' },
            ]}
            value={filterHealth}
            onChange={(e) => setFilterHealth(e.target.value as typeof filterHealth)}
            className="text-xs h-7"
          />
          <Select
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'test', label: 'Tests' },
              { value: 'fixture', label: 'Fixtures' },
            ]}
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as typeof filterType)}
            className="text-xs h-7"
          />
        </div>
        {/* Tag filter chips */}
        {uniqueTags.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Filter by tag:</p>
            <div className="flex flex-wrap gap-1">
              {uniqueTags.map((tag) => {
                const active = filterTags.includes(tag)
                return (
                  <button
                    key={tag}
                    onClick={() =>
                      setFilterTags(active ? filterTags.filter((t) => t !== tag) : [...filterTags, tag])
                    }
                    className={`px-2 py-0.5 text-xs rounded-full transition-colors ${
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {tag}
                    {active && <span className="ml-1">×</span>}
                  </button>
                )
              })}
              {filterTags.length > 0 && (
                <button
                  onClick={() => setFilterTags([])}
                  className="px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">
          Showing {filtered.length} of {cases.length} tests
          {suites.length > 0 ? ` · ${suites.length} suites` : ''}
        </p>
      </div>

      {/* Grouped test list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {groups.length === 0 && (
          <p className="text-xs text-muted-foreground px-2 py-4 text-center">
            No tests match the filters.
          </p>
        )}
        {groups.map((g) => {
          const collapsed = collapsedGroups.has(g.key)
          const ChevIcon = collapsed ? ChevronRight : ChevronDown
          const headerIsSuiteSelector = Boolean(g.suite)
          const suiteSelected = headerIsSuiteSelector && selection?.kind === 'suite' && selection.id === g.suite!.id
          return (
            <div key={g.key}>
              {/* Group header. Suite headers do double duty — chevron
                  collapses, the label area selects the suite. */}
              {(groupBy !== 'none' || groups.length > 1) && (
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] uppercase tracking-wider font-semibold ${
                    suiteSelected ? selectedSidebarClasses : 'text-muted-foreground hover:bg-muted/40'
                  }`}
                >
                  <button
                    onClick={() => toggleGroup(g.key)}
                    className="p-0.5 -ml-0.5 rounded hover:bg-muted/60"
                    aria-label={collapsed ? `Expand ${g.label}` : `Collapse ${g.label}`}
                  >
                    <ChevIcon className="h-3 w-3" />
                  </button>
                  {headerIsSuiteSelector ? (
                    <button
                      onClick={() => onSelect({ kind: 'suite', id: g.suite!.id })}
                      className="flex-1 text-left truncate normal-case tracking-normal text-xs"
                      title={g.suite!.name}
                    >
                      {g.label}
                    </button>
                  ) : (
                    <span className="flex-1 truncate">{g.label}</span>
                  )}
                  <span className="text-[10px] text-muted-foreground">{g.count}</span>
                  {/* Everything about this suite in one place (#2194).
                      Creating one was a button at the top right, renaming
                      meant opening it, deleting meant opening it and finding
                      another control — three places for one thing. Clicking
                      the row still opens it, and Edit tests is that same
                      opening, repeated so the menu is not a list of
                      leftovers. */}
                  {headerIsSuiteSelector && (
                    <KebabMenu
                      ariaLabel={`More actions for ${g.suite!.name}`}
                      width="narrow"
                      className="w-5 h-5"
                      items={[
                        { key: 'rename', label: 'Rename', onClick: () => onRenameSuite(g.suite!.id) },
                        { key: 'edit', label: 'Edit tests', onClick: () => onSelect({ kind: 'suite', id: g.suite!.id }) },
                        {
                          key: 'delete',
                          label: 'Delete suite',
                          danger: true,
                          separator: true,
                          onClick: () => onDeleteSuite(g.suite!),
                        },
                      ]}
                    />
                  )}
                </div>
              )}
              {!collapsed && (
                <div className="space-y-0.5 mt-0.5">
                  {g.cases.map((c, idx) => {
                    const merged = scriptByCaseId[c.id]
                    const { Icon: HealthIcon, cls: healthCls } = healthIcon({
                      lastPassedAt: merged?.lastPassedAt,
                      lastFailedAt: merged?.lastFailedAt,
                    })
                    const isActive = selection?.kind === 'test' && selection.id === c.id
                    return (
                      <button
                        key={c.id}
                        onClick={() => onSelect({ kind: 'test', id: c.id })}
                        aria-pressed={isActive}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                          isActive ? selectedSidebarClasses : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] text-muted-foreground w-4 shrink-0 mt-0.5">{idx + 1}.</span>
                          <HealthIcon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${healthCls}`} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium leading-tight truncate" title={c.name}>{c.name}</p>
                            <div className="flex items-center gap-1.5 mt-1">
                              <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded ${PRIORITY_COLORS[c.priority] ?? PRIORITY_COLORS.P3}`}>
                                {c.priority}
                              </span>
                              {c.type === 'fixture' && (
                                <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/10 text-amber-500 border border-amber-500/30">
                                  fixture
                                </span>
                              )}
                              <span className="text-[10px] text-muted-foreground truncate">{c.intent}</span>
                              {/* #2160 — age only, no label. This column is
                                  narrow and already carries an index, a health
                                  icon, a priority pill and the intent, so
                                  "Created 2d ago" would not fit. The bare age
                                  is enough to spot a stale test while
                                  scanning; the panel states both dates in
                                  full. `ml-auto` pins it right so the ages
                                  line up down the list and can be compared at
                                  a glance. */}
                              {c.createdAt && (
                                <span
                                  className="text-[10px] text-muted-foreground shrink-0 ml-auto"
                                  title={`Created ${formatDateTime(c.createdAt, 'absoluteUTC')}`}
                                >
                                  {formatDateTime(c.createdAt, 'relative').replace(/ ago$/, '')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                  {g.cases.length === 0 && headerIsSuiteSelector && !searchText && (
                    <p className="text-[10px] text-muted-foreground px-3 italic">
                      Empty suite — open to add tests
                    </p>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Re-export for use in index.tsx if it needs to introspect.
export { NO_SUITE_KEY }

// Make TypeScript happy if Input is later swapped in. Keeps the
// import non-broken under a future shake.
void Input
